// Cockpit revamp — top-level layout.
//
// Replaces the legacy `CampaignDetailPage.tsx` with a 3-column cockpit:
//
//   left rail  — Identity / Audience / Sequence / AI context cards
//   center     — Tabbed content (Funnel / Goals / Enrolments / Activity)
//   right rail — Status lifecycle + Recent audit
//
// State management:
//   - `campaign` — the canonical campaign row, refreshed after every
//     audited write (transition / inline edit).
//   - `audit` — read-through useCampaignAudit hook; refreshes after
//     every audited write so the right rail + Activity tab reflect the
//     new entry without a manual reload.
//
// Tab persistence: the active tab is stored in the URL query string so
// the back/forward buttons round-trip correctly.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useNavigate,
  useParams,
  useSearchParams,
  Link,
} from 'react-router-dom';
import { ArrowLeft, Copy, Download, Edit2, History } from 'lucide-react';

import PageShell from '../../../components/PageShell';
import EditableTitle from '../../../components/EditableTitle';
import { Card, Tabs, type TabItem } from '../../../ui';
import {
  campaignsAPI,
  extractCampaignApiError,
  type CampaignReportPayload,
} from '../../../services/campaignsAPI';
import type { Campaign } from '../../../types/campaign';
import type { CampaignKpiProgress } from '../CampaignGoalsKpis';
import type { CampaignEnrolment } from '../CampaignEnrolments';
import { CampaignStatusPill } from '../CampaignsListPage';

import CampaignActivityTab from './CampaignActivityTab';
import CampaignCompletionReport from './CampaignCompletionReport';
import CampaignEnrolmentsTab from './CampaignEnrolmentsTab';
import CampaignFunnel from './CampaignFunnel';
import CampaignGoalsTab from './CampaignGoalsTab';
import CampaignKpiStrip from './CampaignKpiStrip';
import CampaignLeftRail from './CampaignLeftRail';
import CampaignStatusActions from './CampaignStatusActions';
import CampaignStatusLifecycle from './CampaignStatusLifecycle';
import { isPreLaunch } from './transitions';
import { useCampaignAudit } from './useCampaignAudit';
import AuditRow from './AuditRow';

const TAB_VALUES = ['funnel', 'goals', 'enrolments', 'activity', 'report'] as const;
type CockpitTab = (typeof TAB_VALUES)[number];

function tabFromSearch(s: URLSearchParams): CockpitTab {
  const t = s.get('tab') as CockpitTab | null;
  return t && TAB_VALUES.includes(t) ? t : 'funnel';
}

const EmptyDraftCard: React.FC<{ title: string; sub?: string }> = ({
  title,
  sub,
}) => (
  <div className="border border-dashed border-white/[0.12] rounded-ds-md bg-white/[0.02] py-12 text-center">
    <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">
      pre-launch
    </div>
    <div className="text-sm text-slate-200 font-medium mb-1">{title}</div>
    {sub ? (
      <div className="text-xs text-slate-500 max-w-md mx-auto">{sub}</div>
    ) : null}
  </div>
);

const CampaignCockpitPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [report, setReport] = useState<CampaignReportPayload | null>(null);
  const [kpiProgress, setKpiProgress] = useState<CampaignKpiProgress[]>([]);
  const [enrolments, setEnrolments] = useState<CampaignEnrolment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cloning, setCloning] = useState(false);
  const [cloneError, setCloneError] = useState<string | null>(null);
  // LT-627 — campaign CSV export (works in every status).
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const { audit, refresh: refreshAudit } = useCampaignAudit(id);

  const tab = tabFromSearch(searchParams);

  const setTab = (next: CockpitTab) => {
    const sp = new URLSearchParams(searchParams);
    sp.set('tab', next);
    setSearchParams(sp, { replace: true });
  };

  const loadAll = useCallback(() => {
    if (!id) return;
    setLoading(true);
    Promise.all([
      campaignsAPI.get(id),
      campaignsAPI.report(id).catch(() => null),
      campaignsAPI.kpis(id).catch(() => ({ kpis: [] as CampaignKpiProgress[] })),
      campaignsAPI
        .enrolments(id)
        .catch(() => [] as CampaignEnrolment[]),
    ])
      .then(([c, r, k, e]) => {
        setCampaign(c);
        setReport(r);
        setKpiProgress(k.kpis ?? []);
        setEnrolments(e);
        setError(null);
      })
      .catch((err) => {
        setError(extractCampaignApiError(err, 'Failed to load campaign.'));
      })
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(loadAll, [loadAll]);

  const onCampaignChange = useCallback((next: Campaign) => {
    setCampaign(next);
    // Refresh report + KPIs in the background — a transition or inline
    // edit might have shifted live aggregates.
    if (next.id) {
      campaignsAPI.report(next.id).then(setReport).catch(() => undefined);
      campaignsAPI
        .kpis(next.id)
        .then((k) => setKpiProgress(k.kpis ?? []))
        .catch(() => undefined);
    }
  }, []);

  // LT-457 — duplicate the campaign to a fresh draft. Works in every
  // status; on success we jump straight to the new draft's cockpit.
  const handleDuplicate = useCallback(() => {
    if (!campaign || cloning) return;
    setCloning(true);
    setCloneError(null);
    campaignsAPI
      .clone(campaign.id)
      .then((clone) => {
        navigate(`/campaigns/${clone.id}`);
      })
      .catch((err) => {
        setCloneError(
          extractCampaignApiError(err, 'Failed to duplicate campaign.'),
        );
      })
      .finally(() => setCloning(false));
  }, [campaign, cloning, navigate]);

  // LT-627 — export the campaign's full call detail as CSV. Works in every
  // status (active campaigns export their calls-so-far; completed campaigns
  // export the full set). Reuses the run-export download idiom via
  // campaignsAPI.downloadCsv.
  const handleExportCsv = useCallback(() => {
    if (!campaign || exporting) return;
    setExporting(true);
    setExportError(null);
    campaignsAPI
      .downloadCsv(campaign.id)
      .catch((err) => {
        setExportError(extractCampaignApiError(err, 'Failed to export campaign CSV.'));
      })
      .finally(() => setExporting(false));
  }, [campaign, exporting]);

  const preLaunch = useMemo(
    () => (campaign ? isPreLaunch(campaign.status) : false),
    [campaign],
  );

  // LT-460 — rename a draft campaign inline. Locked once launched: the
  // backend's editable-field set still allows `name` post-launch, but the
  // cockpit only offers the affordance pre-launch per the product rule.
  const handleRename = useCallback(
    async (next: string) => {
      if (!campaign) return;
      const updated = await campaignsAPI.update(campaign.id, { name: next });
      onCampaignChange(updated);
    },
    [campaign, onCampaignChange],
  );

  if (loading) {
    return (
      <PageShell>
        <div className="text-sm text-slate-500 px-3 py-6">Loading…</div>
      </PageShell>
    );
  }

  if (error || !campaign) {
    return (
      <PageShell>
        <div className="px-3 py-6">
          <button
            type="button"
            onClick={() => navigate('/campaigns')}
            className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-200 mb-4"
          >
            <ArrowLeft size={14} /> Back to campaigns
          </button>
          <div className="px-3 py-2 rounded-ds-lg border border-danger-border bg-danger-tint text-danger-text text-sm">
            {error ?? 'Campaign not found.'}
          </div>
        </div>
      </PageShell>
    );
  }

  const tabItems: TabItem[] = [
    { value: 'funnel', label: 'Funnel' },
    { value: 'goals', label: 'Goals & KPIs' },
    { value: 'enrolments', label: 'Enrolments' },
    { value: 'activity', label: 'Activity' },
    { value: 'report', label: 'Completion Report' },
  ];

  const activeEnrolments = report?.active.active_count ?? null;

  return (
    <PageShell>
      <button
        type="button"
        onClick={() => navigate('/campaigns')}
        className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-200 mb-2"
      >
        <ArrowLeft size={14} /> Back to campaigns
      </button>

      <header className="mb-4 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-1">
            Campaign
          </div>
          <div className="flex items-center gap-3">
            <EditableTitle
              value={campaign.name}
              editable={campaign.status === 'draft'}
              onSave={handleRename}
              className="text-2xl font-semibold text-slate-50"
              ariaLabel="Campaign name"
            />
            <CampaignStatusPill status={campaign.status} />
          </div>
          {campaign.description ? (
            <p className="text-sm text-slate-400 mt-1 max-w-2xl">
              {campaign.description}
            </p>
          ) : null}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* LT-457 — duplicate works in every status; the clone is a
              fresh draft so the source is never mutated. */}
          <button
            type="button"
            onClick={handleDuplicate}
            disabled={cloning}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-[13px] rounded-ds-lg border border-white/[0.08] bg-white/5 text-slate-300 hover:bg-white/10 hover:text-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Copy size={14} /> {cloning ? 'Duplicating…' : 'Duplicate'}
          </button>
          {/* LT-627 — export the full call detail as CSV. Available in
              every status, including completed / force-ended. */}
          <button
            type="button"
            onClick={handleExportCsv}
            disabled={exporting}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-[13px] rounded-ds-lg border border-white/[0.08] bg-white/5 text-slate-300 hover:bg-white/10 hover:text-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download size={14} /> {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
          {/* LT-401 — drafts open in the stepped wizard so the operator
              can resume saving across steps. Non-drafts keep the flat
              form (status-aware field locking lives there). */}
          <Link
            to={
              campaign.status === 'draft'
                ? `/campaigns/${campaign.id}/edit-wizard`
                : `/campaigns/${campaign.id}/edit`
            }
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-[13px] rounded-ds-lg border border-white/[0.08] bg-white/5 text-slate-300 hover:bg-white/10 hover:text-slate-50"
          >
            <Edit2 size={14} /> Edit
          </Link>
          <CampaignStatusActions
            campaign={campaign}
            onCampaignChange={onCampaignChange}
            onAuditChanged={() => {
              refreshAudit().catch(() => undefined);
            }}
          />
        </div>
      </header>

      {cloneError ? (
        <div className="mb-4 px-3 py-2 rounded-ds-lg border border-danger-border bg-danger-tint text-danger-text text-sm">
          {cloneError}
        </div>
      ) : null}

      {exportError ? (
        <div className="mb-4 px-3 py-2 rounded-ds-lg border border-danger-border bg-danger-tint text-danger-text text-sm">
          {exportError}
        </div>
      ) : null}

      <CampaignKpiStrip campaign={campaign} isPreLaunch={preLaunch} report={report} />

      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr_280px] gap-3 items-start">
        <aside aria-label="Campaign identity">
          <CampaignLeftRail
            campaign={campaign}
            isPreLaunch={preLaunch}
            activeEnrolments={activeEnrolments}
            onCampaignChange={onCampaignChange}
            onAuditChanged={() => {
              refreshAudit().catch(() => undefined);
            }}
          />
        </aside>

        <Card padding="tight">
          <div className="px-1 -mx-1 mb-3">
            <Tabs
              items={tabItems}
              value={tab}
              onChange={(v) => setTab(v as CockpitTab)}
              aria-label="Campaign cockpit"
            />
          </div>
          <div className="px-1 pb-1">
            {tab === 'funnel'
              ? preLaunch
                ? <EmptyDraftCard title="Funnel will populate after launch" />
                : <CampaignFunnel report={report} enrolments={enrolments} />
              : null}
            {tab === 'goals' ? (
              <CampaignGoalsTab
                campaign={campaign}
                progress={kpiProgress}
                isPreLaunch={preLaunch}
                onCampaignChange={onCampaignChange}
                onAuditChanged={() => {
                  refreshAudit().catch(() => undefined);
                }}
              />
            ) : null}
            {tab === 'enrolments'
              ? preLaunch
                ? (
                  <EmptyDraftCard
                    title="No enrolments yet"
                    sub="Contacts in the audience will be snapshotted into enrolments when you launch."
                  />
                )
                : <CampaignEnrolmentsTab campaignId={campaign.id} />
              : null}
            {tab === 'activity' ? <CampaignActivityTab audit={audit} /> : null}
            {tab === 'report'
              ? preLaunch
                ? <EmptyDraftCard title="Completion report will be available after the campaign runs and completes" />
                : <CampaignCompletionReport campaignId={campaign.id} />
              : null}
          </div>
        </Card>

        <aside aria-label="Status + audit" className="flex flex-col gap-3">
          <Card padding="tight">
            <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-2">
              Status lifecycle
            </div>
            <CampaignStatusLifecycle current={campaign.status} />
          </Card>
          <Card padding="tight">
            <div className="flex items-center gap-2 mb-2">
              <History size={12} className="text-slate-500" />
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">
                Recent audit
              </div>
            </div>
            {audit.length === 0 ? (
              <p className="text-xs text-slate-500 italic">No audit events yet.</p>
            ) : (
              <ol className="flex flex-col divide-y divide-white/[0.04]">
                {audit.slice(0, 4).map((entry) => (
                  <li key={entry.id}>
                    <AuditRow entry={entry} compact />
                  </li>
                ))}
              </ol>
            )}
            {audit.length > 4 ? (
              <button
                type="button"
                onClick={() => setTab('activity')}
                className="block w-full mt-2 text-[11px] text-accent hover:text-accent-hover underline-offset-2 hover:underline text-center"
              >
                View all ({audit.length}) →
              </button>
            ) : null}
          </Card>
        </aside>
      </div>

    </PageShell>
  );
};

export default CampaignCockpitPage;
